import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const FloatingAssessmentButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling past the hero section (approximately 500px)
      const scrollY = window.scrollY;
      setIsVisible(scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = () => {
    // If on homepage, scroll to assessment
    if (location.pathname === '/') {
      const element = document.getElementById('investment-assessment');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to homepage with hash
      navigate('/#investment-assessment');
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDismissed(true);
    // Reset after some time
    setTimeout(() => setIsDismissed(false), 60000); // Reset after 1 minute
  };

  if (isDismissed) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: 100, scale: 0.8 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 100, scale: 0.8 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          className="fixed right-6 bottom-24 md:bottom-32 z-40"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Dismiss button */}
          <AnimatePresence>
            {isHovered && (
              <motion.button
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                onClick={handleDismiss}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-muted border border-border flex items-center justify-center z-10 hover:bg-destructive/10 transition-colors"
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </motion.button>
            )}
          </AnimatePresence>

          {/* Main button */}
          <motion.button
            onClick={handleClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative group"
          >
            {/* Pulse ring */}
            <motion.span
              className="absolute inset-0 rounded-full bg-gold/30"
              animate={{
                scale: [1, 1.4, 1],
                opacity: [0.5, 0, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />

            {/* Button container */}
            <div className="relative flex items-center gap-3 bg-gradient-gold text-cream px-5 py-3 rounded-full shadow-lg shadow-gold/30">
              <Crown className="h-5 w-5" />
              
              {/* Expanded text on hover */}
              <AnimatePresence>
                {isHovered ? (
                  <motion.span
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 'auto', opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="font-medium text-sm overflow-hidden whitespace-nowrap"
                  >
                    Start Assessment
                  </motion.span>
                ) : (
                  <motion.span
                    initial={{ width: 'auto', opacity: 1 }}
                    animate={{ width: 'auto', opacity: 1 }}
                    className="font-medium text-sm"
                  >
                    Assess
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </motion.button>

          {/* Tooltip on first appearance */}
          <AnimatePresence>
            {!isHovered && isVisible && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ delay: 1, duration: 0.3 }}
                className="absolute right-0 bottom-full mb-3 pointer-events-none"
              >
                <div className="bg-card border border-border/50 rounded-lg px-3 py-2 shadow-lg min-w-[180px]">
                  <p className="text-xs text-foreground font-medium">Investment Assessment</p>
                  <p className="text-xs text-muted-foreground">Get personalized recommendations</p>
                  {/* Arrow */}
                  <div className="absolute top-full right-6 w-2 h-2 bg-card border-r border-b border-border/50 transform rotate-45 -translate-y-1" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingAssessmentButton;
